import { type SiteId } from '@solinkcorp/api-types';
import {
  LocationsFilterButton,
  LocationsFilterIconButton,
} from '@solinkcorp/solink-ui/components/LocationsFilter';
import { Popover, PopoverContent, PopoverTrigger } from '@solinkcorp/solink-ui/components/Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@solinkcorp/solink-ui/components/Tooltip';
import { useSidebar } from '@solinkcorp/solink-ui/components/ui/sidebar';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import MultiLocationSelector from 'components/LocationSelector/Aspen/MultiLocationSelector';
import { type SELECT_LOCATIONS_CONTEXT } from 'models/AnalyticsConstants';
import { type FC, useEffect, useMemo, useState } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import useTabName from 'src/hooks/useTabName';
import { LocationStore } from 'stores/LocationStore';
import { TAB_VIDEO } from 'vd-constants';

export interface AspenLocationsFilterProps {
  variant: 'expanded' | 'collapsed';
  selectedLocationIds?: readonly SiteId[];
  selectedLocationTags?: readonly string[];
  onLocationsSelected?: ({
    locationIds,
    locationTags,
  }: {
    locationIds: readonly SiteId[];
    locationTags?: readonly string[];
  }) => void;
  context?:
    typeof SELECT_LOCATIONS_CONTEXT.GLOBAL_SELECTOR | typeof SELECT_LOCATIONS_CONTEXT.WIDGET;
  disabled?: boolean;
}

/**
 * Aspen sidebar locations filter: solink-ui chrome + legacy MultiLocationSelector list.
 */
const AspenLocationsFilter: FC<AspenLocationsFilterProps> = ({
  variant,
  selectedLocationIds,
  selectedLocationTags,
  onLocationsSelected,
  context,
  disabled,
}) => {
  const locale = useLocale();
  const { state: sidebarState } = useSidebar();
  const [open, setOpen] = useState(false);

  // Content portals to document.body, so hiding the trigger on collapse would
  // otherwise leave a floating list with no visible anchor.
  useEffect(() => {
    const triggerVisible =
      (variant === 'expanded' && sidebarState !== 'collapsed') ||
      (variant === 'collapsed' && sidebarState === 'collapsed');
    if (!triggerVisible) setOpen(false);
  }, [sidebarState, variant]);

  const allLocations = useFluxStore(LocationStore, (store) => store.getLocations());
  const ready = useFluxStore(LocationStore, (store) => store.getIsLocationsReady());
  const fetching = useFluxStore(LocationStore, (store) => !store.getHasFetchedLocations());

  const hasAccessToLocations =
    allLocations.length > 1 || (allLocations.length === 1 && allLocations[0].id !== ALL_LOCATIONS);

  const label = useMemo(
    () =>
      getLocationsFilterLabel({
        locationIds: selectedLocationIds,
        locationsList: allLocations,
        loading: !ready,
        noLocationAccess: !hasAccessToLocations,
        loadingLabel: locale.general.loading,
        noLocationsLabel: locale.general.noLocations,
        locationsWord: locale.general.locations,
      }),
    [
      selectedLocationIds,
      allLocations,
      ready,
      hasAccessToLocations,
      locale.general.loading,
      locale.general.noLocations,
      locale.general.locations,
    ],
  );

  const tabName = useTabName();
  const isVideoTab = tabName === TAB_VIDEO;
  const locationsForList = useMemo(() => {
    const withoutAll = allLocations.filter((location) => location.id !== ALL_LOCATIONS);
    return isVideoTab ? withoutAll.filter((location) => !!location.cameras?.length) : withoutAll;
  }, [allLocations, isVideoTab]);

  const isDisabled = disabled || !ready || !hasAccessToLocations;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {variant === 'collapsed' ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <LocationsFilterIconButton aria-label={label} disabled={isDisabled} isActive={open} />
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            {label}
          </TooltipContent>
        </Tooltip>
      ) : (
        <PopoverTrigger asChild>
          <LocationsFilterButton disabled={isDisabled}>{label}</LocationsFilterButton>
        </PopoverTrigger>
      )}
      <PopoverContent
        className="!w-auto !p-0"
        side={variant === 'collapsed' ? 'right' : 'bottom'}
        align="start"
        collisionPadding={4}
        data-aspen-locations-filter
      >
        <MultiLocationSelector
          locations={locationsForList}
          locale={locale}
          selectedLocationIds={selectedLocationIds}
          selectedLocationTags={selectedLocationTags}
          onLocationsSelected={onLocationsSelected}
          closeLocationList={() => setOpen(false)}
          context={context}
          isFetching={fetching}
        />
      </PopoverContent>
    </Popover>
  );
};

/** Label text for the filter trigger — mirrors SelectedLocations name logic (no tag pills). */
export function getLocationsFilterLabel({
  locationIds,
  locationsList,
  loading,
  noLocationAccess,
  loadingLabel,
  noLocationsLabel,
  locationsWord,
}: {
  locationIds?: readonly string[];
  locationsList: readonly { id: string; name: string }[];
  loading: boolean;
  noLocationAccess: boolean;
  loadingLabel: string;
  noLocationsLabel: string;
  locationsWord: string;
}): string {
  if (loading) return loadingLabel;
  if (noLocationAccess) return noLocationsLabel;
  if (!locationIds) return '';

  if (locationIds.length > 1) {
    return `${locationIds.length} ${locationsWord}`;
  }

  const selected = locationIds[0]
    ? locationsList.find((location) => location.id === locationIds[0])
    : locationsList.find((location) => location.id === ALL_LOCATIONS);

  return selected?.name ?? `${locationIds[0] ?? ''}`;
}

export default AspenLocationsFilter;
